import React, { useState } from "react";
import PropTypes from "prop-types";
import { FiMail, FiUser } from "react-icons/fi";
import { notifyError, notifySuccess } from "@utils/toast";

const NewsletterSignup = ({
    variant = "default",
    title = "Stay Updated!",
    description = "Subscribe to latest offers, competition announcements, and personalized discount emails",
    className = "",
    useMagicLink = false // New prop to determine if we should use magic link or direct subscription
}) => {
    const [formData, setFormData] = useState({
        name: "",
        email: ""
    });
    const [isLoading, setIsLoading] = useState(false);
    
    // Add unique identifier to track component instances
    const componentId = React.useRef(Math.random().toString(36).substr(2, 9)).current;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prevent multiple submissions
        if (isLoading) {
            return;
        }

        if (!formData.name.trim() || !formData.email.trim()) {
            notifyError("Please fill in all fields");
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            notifyError("Please enter a valid email address");
            return;
        }

        setIsLoading(true);        try {
            const endpoint = useMagicLink ? '/api/newsletter/magic-link' : '/api/newsletter/subscribe';
            console.log('🚀 Newsletter submission started:', { componentId, endpoint, formData, timestamp: new Date().toISOString() });
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();
            console.log('📦 API response received:', { 
                status: response.status, 
                ok: response.ok, 
                result, 
                timestamp: new Date().toISOString()            });

            if (response.ok && result.success) {
                console.log('✅ Showing success toast:', result.message);
                notifySuccess(result.message);
                setFormData({ name: "", email: "" }); // Reset form
            } else {
                console.log('❌ Showing error toast:', result.message);
                notifyError(result.message || "Failed to subscribe. Please try again.");
            }
        } catch (error) {
            console.error('Newsletter subscription error:', error);
            notifyError("Something went wrong. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    if (variant === "compact") {
        return (
            <div className={`bg-white rounded-lg p-4 shadow-md ${className}`}>
                <div className="text-center mb-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">{title}</h4>
                    <p className="text-sm text-gray-600">{description}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="relative">
                        <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your name"
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="relative">
                        <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your-email@example.com"
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            disabled={isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-medium py-2 px-4 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                        {isLoading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Subscribing...
                            </>
                        ) : (
                            <>
                                <FiMail className="mr-2" />
                                Subscribe Now
                            </>
                        )}
                    </button>
                </form>
            </div>
        );
    }

    // Default variant - full form
    return (
        <div className={`bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 border border-emerald-200 ${className}`}>
            <div className="text-center mb-6">
                <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiMail className="text-white text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                    <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        disabled={isLoading}
                    />
                </div>

                <div className="relative">
                    <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your-email@example.com"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        disabled={isLoading}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                    {isLoading ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Subscribing...
                        </>
                    ) : (
                        <>
                            <FiMail className="mr-2" />
                            Subscribe to Newsletter
                        </>
                    )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                    You'll receive a confirmation email. Unsubscribe anytime.
                </p>
            </form>
        </div>
    );
};

NewsletterSignup.propTypes = {
    variant: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    className: PropTypes.string,
    useMagicLink: PropTypes.bool
};

export default NewsletterSignup;
